/**
 * License Economics – management section for the Forecast page.
 *
 * • Lists all records (activeOnly:false) so retired records remain reviewable.
 * • Separates active and retired rows with clear visual distinction.
 * • Drawer is tabbed: Details (create/edit form) and Audit (history).
 * • Mutations invalidate License Economics list/detail/audit caches only —
 *   never the Forecast/outlook caches.
 */

import { useMemo, useState } from "react";
import {
  AlertCircle,
  Archive,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  ScrollText,
  X,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListLicenseEconomics,
  getListLicenseEconomicsQueryKey,
  useCreateLicenseEconomics,
  useUpdateLicenseEconomics,
  useRetireLicenseEconomics,
  getGetLicenseEconomicsQueryKey,
  getGetLicenseEconomicsAuditQueryKey,
} from "@workspace/api-client-react";
import type {
  LicenseEconomics,
} from "@workspace/api-client-react";
import { formatMoney } from "@/components/tekledger-ui";
import { LicenseEconomicsForm } from "./form";
import type { LicenseEconomicsFormPayload } from "./form";
import { LicenseEconomicsAuditPanel } from "./audit";
import {
  LE_ACTOR,
  LICENSE_MODEL_LABELS,
  LE_STATUS_LABELS,
  statusTone,
  summarizeTiers,
  summarizeTrueUp,
} from "./types";
import type { LicenseScope } from "./types";

function errMsg(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "message" in err) {
    return String((err as { message: string }).message);
  }
  return fallback;
}

function unusedSeatPotential(record: LicenseEconomics): number {
  if (record.currentModel === "usage_based") return 0;
  const unused = Math.max(0, record.seats - record.activeSeats);
  const currentRaw =
    record.seats * record.seatRate +
    (record.currentModel === "hybrid"
      ? record.currentMonthlyUsage * record.unitRate
      : 0) +
    record.fixedMonthlyComponent;
  const reducedRaw = currentRaw - unused * record.seatRate;
  return Math.max(
    0,
    Math.max(record.minimumMonthlyCommitment, currentRaw) -
      Math.max(record.minimumMonthlyCommitment, reducedRaw)
  );
}

type DrawerTab = "details" | "audit";

export function LicenseEconomicsSection({
  companyId,
  fiscalYear,
  departmentCode,
}: {
  companyId: number;
  fiscalYear: number;
  departmentCode: string;
}) {
  const scope: LicenseScope = useMemo(
    () => ({ companyId, fiscalYear, departmentCode }),
    [companyId, fiscalYear, departmentCode]
  );

  // activeOnly:false so retired records remain reviewable in the table
  const listParams = useMemo(
    () => ({ ...scope, activeOnly: false }),
    [scope]
  );

  const qc = useQueryClient();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<LicenseEconomics | null>(null);
  const [drawerTab, setDrawerTab] = useState<DrawerTab>("details");
  const [formError, setFormError] = useState("");
  const [retiringId, setRetiringId] = useState<string | null>(null);

  const {
    data: records,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useListLicenseEconomics(listParams, {
    query: { queryKey: getListLicenseEconomicsQueryKey(listParams) },
  });

  const createMutation = useCreateLicenseEconomics();
  const updateMutation = useUpdateLicenseEconomics();
  const retireMutation = useRetireLicenseEconomics();

  const rows = records ?? [];
  const activeRows = rows.filter((r) => r.status !== "retired");
  const retiredRows = rows.filter((r) => r.status === "retired");

  // Invalidate only License Economics caches — never Forecast/outlook caches.
  const invalidateListCache = () => {
    qc.invalidateQueries({ queryKey: getListLicenseEconomicsQueryKey(listParams) });
  };

  const invalidateRecordCaches = (id: string) => {
    qc.invalidateQueries({ queryKey: getGetLicenseEconomicsQueryKey(id) });
    qc.invalidateQueries({ queryKey: getGetLicenseEconomicsAuditQueryKey(id) });
  };

  const openCreate = () => {
    setEditing(null);
    setFormError("");
    setDrawerTab("details");
    setDrawerOpen(true);
  };

  const openEdit = (record: LicenseEconomics, tab: DrawerTab = "details") => {
    setEditing(record);
    setFormError("");
    setDrawerTab(tab);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditing(null);
    setFormError("");
  };

  const handleFormSubmit = (payload: LicenseEconomicsFormPayload) => {
    setFormError("");

    if (payload.mode === "edit" && editing) {
      updateMutation.mutate(
        { licenseEconomicsId: editing.licenseEconomicsId, data: payload.update },
        {
          onSuccess: (updated) => {
            qc.setQueryData(
              getGetLicenseEconomicsQueryKey(editing.licenseEconomicsId),
              updated
            );
            invalidateListCache();
            invalidateRecordCaches(editing.licenseEconomicsId);
            closeDrawer();
          },
          onError: (err) =>
            setFormError(errMsg(err, "Failed to update record.")),
        }
      );
    } else if (payload.mode === "create") {
      createMutation.mutate(
        { data: payload.input },
        {
          onSuccess: () => {
            invalidateListCache();
            closeDrawer();
          },
          onError: (err) =>
            setFormError(errMsg(err, "Failed to create record.")),
        }
      );
    }
  };

  const handleRetire = (record: LicenseEconomics) => {
    setRetiringId(record.licenseEconomicsId);
    retireMutation.mutate(
      {
        licenseEconomicsId: record.licenseEconomicsId,
        data: { actor: LE_ACTOR },
      },
      {
        onSuccess: () => {
          invalidateListCache();
          invalidateRecordCaches(record.licenseEconomicsId);
          setRetiringId(null);
        },
        onError: () => setRetiringId(null),
      }
    );
  };

  return (
    <div className="card le-section" data-testid="license-economics-section">
      <div className="card-head">
        <div>
          <div className="eyebrow">Scenario Inputs</div>
          <h3>LICENSE ECONOMICS</h3>
          <p className="le-section__sub">
            Review utilization and identify reclaim opportunities. Active seats
            are evidence only; savings are created explicitly in Scenarios.
          </p>
        </div>
        <div className="le-section__actions">
          <button
            className="tl-btn"
            onClick={() => refetch()}
            disabled={isFetching}
            data-testid="button-le-refresh"
            title="Reload records"
          >
            <RefreshCw size={12} className={isFetching ? "animate-spin" : ""} />
          </button>
          <button
            className="tl-btn primary"
            onClick={openCreate}
            data-testid="button-le-new"
          >
            <Plus size={13} /> New record
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="le-loading" data-testid="license-economics-loading">
          <Loader2 size={16} className="animate-spin" /> Loading license
          economics…
        </div>
      )}

      {isError && !isLoading && (
        <div
          className="sc-form-error"
          role="alert"
          data-testid="license-economics-error"
        >
          <AlertCircle size={14} />
          <div>
            <strong>Could not load license economics.</strong>
            <button
              className="tl-btn"
              onClick={() => refetch()}
              style={{ marginLeft: 12 }}
            >
              <RefreshCw size={12} /> Retry
            </button>
          </div>
        </div>
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <div className="le-empty" data-testid="license-economics-empty">
          <ScrollText size={26} />
          <h4>No license economics yet</h4>
          <p>
            Create a reusable license model to test model changes (seat-based,
            usage-based, or hybrid) inside scenarios without touching the
            Forecast.
          </p>
          <button className="tl-btn primary" onClick={openCreate}>
            <Plus size={13} /> Create first record
          </button>
        </div>
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <>
          <div className="table-wrap">
            <table className="tl-fy-table le-table">
              <thead>
                <tr>
                  <th>Vendor / Workload</th>
                  <th>GL</th>
                  <th>Model change</th>
                  <th className="num">Utilization</th>
                  <th className="num">Reclaim potential / mo.</th>
                  <th>Tiered terms</th>
                  <th>True-up</th>
                  <th>Status</th>
                  <th className="le-table__actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Active rows first */}
                {activeRows.map((r) => (
                  <tr
                    key={r.licenseEconomicsId}
                    data-testid={`le-row-${r.licenseEconomicsId}`}
                  >
                    <td>
                      <div className="le-cell-primary">{r.vendor}</div>
                      <div className="le-cell-secondary">
                        {r.productWorkload}
                      </div>
                    </td>
                    <td>
                      <code className="le-code">{r.glAccount}</code>
                    </td>
                    <td>
                      <span className="le-model-flow">
                        {LICENSE_MODEL_LABELS[r.currentModel]}
                        <span className="le-model-arrow"> → </span>
                        {LICENSE_MODEL_LABELS[r.futureModel]}
                      </span>
                    </td>
                    <td className="num">
                      {r.activeSeats} / {r.seats}
                      <div className="le-cell-secondary">{r.seats ? Math.round(r.activeSeats / r.seats * 100) : 0}% active</div>
                    </td>
                    <td className="num">
                      <strong className={r.seats > r.activeSeats ? "good-text" : ""}>
                        {formatMoney(unusedSeatPotential(r))}
                      </strong>
                    </td>
                    <td>
                      <div className="le-cell-secondary">Seats: {summarizeTiers(r.seatTiers, r.seatRate, "seat")}</div>
                      <div className="le-cell-secondary">Usage: {summarizeTiers(r.usageTiers, r.unitRate, r.usageUnit || "unit")}</div>
                    </td>
                    <td>
                      <span className="le-cell-secondary" data-testid={`text-le-true-up-${r.licenseEconomicsId}`}>
                        {summarizeTrueUp(r)}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`source-status ${statusTone(r.status)}`}
                      >
                        {LE_STATUS_LABELS[
                          (r.status as keyof typeof LE_STATUS_LABELS) ?? "draft"
                        ] ?? r.status}
                      </span>
                    </td>
                    <td className="le-table__actions-col">
                      <div className="le-row-actions">
                        <button
                          className="sc-icon-btn"
                          onClick={() => openEdit(r, "details")}
                          title="Edit"
                          aria-label="Edit record"
                          data-testid={`button-le-edit-${r.licenseEconomicsId}`}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          className="sc-icon-btn"
                          onClick={() => openEdit(r, "audit")}
                          title="View audit history"
                          aria-label="View audit history"
                          data-testid={`button-le-audit-${r.licenseEconomicsId}`}
                        >
                          <ScrollText size={14} />
                        </button>
                        <button
                          className="sc-icon-btn"
                          onClick={() => handleRetire(r)}
                          disabled={retiringId === r.licenseEconomicsId}
                          title="Retire record"
                          aria-label="Retire record"
                          data-testid={`button-le-retire-${r.licenseEconomicsId}`}
                        >
                          {retiringId === r.licenseEconomicsId ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Archive size={14} />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {/* Retired rows — visually dimmed, with divider when mixed */}
                {retiredRows.length > 0 && activeRows.length > 0 && (
                  <tr className="le-section-divider">
                    <td colSpan={9}>
                      <span>Retired records</span>
                    </td>
                  </tr>
                )}
                {retiredRows.map((r) => (
                  <tr
                    key={r.licenseEconomicsId}
                    className="le-row--retired"
                    data-testid={`le-row-${r.licenseEconomicsId}`}
                  >
                    <td>
                      <div className="le-cell-primary">{r.vendor}</div>
                      <div className="le-cell-secondary">
                        {r.productWorkload}
                      </div>
                    </td>
                    <td>
                      <code className="le-code">{r.glAccount}</code>
                    </td>
                    <td>
                      <span className="le-model-flow">
                        {LICENSE_MODEL_LABELS[r.currentModel]}
                        <span className="le-model-arrow"> → </span>
                        {LICENSE_MODEL_LABELS[r.futureModel]}
                      </span>
                    </td>
                    <td className="num">
                      {r.activeSeats} / {r.seats}
                      <div className="le-cell-secondary">{r.seats ? Math.round(r.activeSeats / r.seats * 100) : 0}% active</div>
                    </td>
                    <td className="num">
                      {formatMoney(unusedSeatPotential(r))}
                    </td>
                    <td>
                      <div className="le-cell-secondary">Seats: {summarizeTiers(r.seatTiers, r.seatRate, "seat")}</div>
                      <div className="le-cell-secondary">Usage: {summarizeTiers(r.usageTiers, r.unitRate, r.usageUnit || "unit")}</div>
                    </td>
                    <td>
                      <span className="le-cell-secondary">{summarizeTrueUp(r)}</span>
                    </td>
                    <td>
                      <span className="source-status missing">Retired</span>
                    </td>
                    <td className="le-table__actions-col">
                      <div className="le-row-actions">
                        <button
                          className="sc-icon-btn"
                          onClick={() => openEdit(r, "audit")}
                          title="View audit history"
                          aria-label="View audit history"
                          data-testid={`button-le-audit-${r.licenseEconomicsId}`}
                        >
                          <ScrollText size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Slide-over drawer (tabbed: Details / Audit) */}
      {drawerOpen && (
        <>
          <div
            className="fi-drawer-backdrop"
            onClick={closeDrawer}
            data-testid="le-drawer-backdrop"
          />
          <aside
            className="fi-drawer"
            role="dialog"
            aria-modal="true"
            data-testid="license-economics-drawer"
          >
            <div className="fi-drawer-head">
              <div>
                <div className="eyebrow">Scenario input</div>
                <h3>
                  {editing
                    ? `${editing.vendor} — ${editing.productWorkload}`
                    : "New license economics"}
                </h3>
                <p className="fi-drawer-help">
                  FY{fiscalYear} · dept {departmentCode}
                </p>
              </div>
              <button
                onClick={closeDrawer}
                aria-label="Close"
                data-testid="button-le-drawer-close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Tabs — only shown when editing an existing record */}
            {editing && (
              <div className="fi-drawer-tabs">
                <button
                  className={drawerTab === "details" ? "active" : ""}
                  onClick={() => setDrawerTab("details")}
                  data-testid="tab-le-details"
                >
                  Details
                </button>
                <button
                  className={drawerTab === "audit" ? "active" : ""}
                  onClick={() => setDrawerTab("audit")}
                  data-testid="tab-le-audit"
                >
                  Audit
                </button>
              </div>
            )}

            {/* Details tab (also the only view on create) */}
            {(!editing || drawerTab === "details") && (
              <LicenseEconomicsForm
                record={editing}
                scope={scope}
                onSubmit={handleFormSubmit}
                onCancel={closeDrawer}
                isPending={
                  createMutation.isPending || updateMutation.isPending
                }
                error={formError}
              />
            )}

            {/* Audit tab (only for existing records) */}
            {editing && drawerTab === "audit" && (
              <div className="fi-drawer-body">
                <LicenseEconomicsAuditPanel
                  licenseEconomicsId={editing.licenseEconomicsId}
                />
              </div>
            )}
          </aside>
        </>
      )}
    </div>
  );
}
